/* eslint-disable prefer-destructuring */
import React, { useState, useEffect, createRef } from 'react';
import {
    getToken, getCustomer, getSeller, getCompanyList,
} from '@modules/login/services/graphql';
import { custDataNameCookie, sellerDataNameCookie, multipleServices } from '@config';
import { setLogin, getLoginInfo, removeIsLoginFlagging } from '@helper_auth';
import { useRouter } from 'next/router';
import Layout from '@layout';
import { regexEmail } from '@helper_regex';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import Cookies from 'js-cookie';

const ContentWrapper = (props) => {
    const {
        Content, storeLogo, t, recaptcha,
    } = props;
    const router = useRouter();
    const [confirmNotif, setConfirmNotif] = useState(false);

    const [getCompany] = getCompanyList({
        onCompleted: async (res) => {
            if (res.getCompanyList?.items?.length && !Cookies.get('company')) {
                await Cookies.set('company', res.getCompanyList?.items?.[0]);
                await setLogin(1);
            } else {
                setLogin(1);
            }
        },
    });
    const [getSellerData] = getSeller({
        onCompleted: async (resSeller) => {
            await Cookies.set(sellerDataNameCookie, resSeller.getSeller, { expires: 1 });
            await Cookies.set('refreshAcl', 1, { expires: 1 });
            await setLogin(1);
        },
    });
    const [getCustomerData, { data: dataCustomer }] = getCustomer({
        onCompleted: async (res) => {
            Cookies.set(custDataNameCookie, res.customer, { expires: 1 });
            const storeModalCookies = await Cookies.get('store_integration');
            if (storeModalCookies !== 'skip') {
                await Cookies.remove('store_integration');
            }
            if (res.customer?.customer_company_code) {
                await getSellerData();
            } else {
                await Cookies.set('refreshAcl', 1, { expires: 1 });
                if (multipleServices.pim) {
                    getCompany();
                } else {
                    await setLogin(1);
                }
            }
        },
    });

    const [getCustomerToken] = getToken();

    const recaptchaRef = createRef();

    const handleSubmit = (variables, captcha = '') => {
        getCustomerToken({
            variables, context: { request: 'internal', headers: { 'x-recaptcha': captcha } },
        }).then(async (res) => {
            const tokenRes = res.data.internalGenerateCustomerToken.token;
            if (tokenRes) {
                getCustomerData();
            }
        }).catch((e) => {
            window.backdropLoader(false);
            if (e.message.search('graphql-customer-unconfirmed') >= 0) {
                setConfirmNotif(true);
            } else {
                setConfirmNotif(false);
                window.toastMessage({
                    open: true,
                    variant: 'error',
                    text: e.message.split(':')[0],
                });
            }
        });
    };

    const formik = useFormik({
        initialValues: {
            email: '',
            password: '',
            captcha: '',
        },
        validationSchema: Yup.object().shape({
            email: Yup.string().required(t('login:This_is_a_Required_field')).matches(regexEmail, t('login:Invalid email format!')),
            password: Yup.string().required(t('login:This_is_a_Required_field')),
            captcha: recaptcha.enable && Yup.string().required(`Captcha ${t('registervendor:required')}`),
        }),
        onSubmit: async (values) => {
            const { captcha, ...restValues } = values;
            window.backdropLoader(true);
            if (recaptcha.enable) {
                try {
                    handleSubmit(restValues, captcha);
                } catch {
                    window.backdropLoader(false);
                    window.toastMessage({
                        open: true,
                        variant: 'error',
                        text: t('login:Could_not_verify_captcha'),
                    });
                    recaptchaRef.current.reset();
                }
            } else {
                handleSubmit(restValues);
            }
        },
    });

    useEffect(() => {
        if (router.query?.confirm) {
            if (router.query?.confirm === 'success') {
                window.toastMessage({
                    open: true,
                    text: t('login:Thank_you_for_registering_Please_log_in_to_your_account'),
                    variant: 'success',
                });
            } else if (router.query?.confirm === 'failed' && Cookies.get('error_confirmation')) {
                window.toastMessage({
                    open: true,
                    variant: 'error',
                    text: Cookies.get('error_confirmation'),
                });
            }
        }
    }, []);

    useEffect(() => {
        if (getLoginInfo()) {
            if (dataCustomer?.customer?.customer_company_code) {
                router.push('/seller/order/orderlist');
            } else {
                router.push('/');
            }
        }
    });

    const contentProps = {
        ...props,
        formik,
        storeLogo,
        t,
        recaptchaRef,
        confirmNotif,
    };

    useEffect(() => {
        removeIsLoginFlagging();
    }, []);

    return (
        <Content {...contentProps} />
    );
};

const Core = (props) => {
    const { t, storeConfig } = props;

    const pageConfig = {
        title: t('login:Login'),
        header: false,
        sidebar: false,
    };

    const contentProps = {
        ...props,
        recaptcha: {
            enable: storeConfig?.recaptcha_for_customer_login === 'invisible' || storeConfig?.recaptcha_for_customer_login === 'recaptcha',
            sitekey: storeConfig?.recaptcha_for_customer_login === 'invisible'
                ? storeConfig?.recaptcha_v2_invisible_public_key : storeConfig?.recaptcha_v2_public_key,
        },
        dataConfig: storeConfig?.swiftoms_enable_vendor_portal === '1',
    };

    return (
        <Layout pageConfig={pageConfig}>
            <ContentWrapper
                {...contentProps}
            />
        </Layout>
    );
};

export default Core;
